<template>
  <validation-provider v-slot="{ errors }" :vid="vid" :name="label" :rules="rulesComputed" :debounce="debounce">
    <v-autocomplete
      class="select-single-input"
      v-model="model"
      :label="fullLabel"
      :items="itemsComputed"
      :item-text="itemText"
      :item-value="itemValue"
      outlined
      dense
      hide-details="auto"
      hide-no-data
      :clearable="clearable"
      clear-icon="mdi-close"
      :filter="customFilter"
      :error-messages="errors"
      :loading="loading"
      :disabled="disabled"
      @change="onChange"
      @update:search-input="onSearchInput"
    ></v-autocomplete>
  </validation-provider>
</template>

<script>
import { ValidationProvider } from 'vee-validate'
import differenceWith from 'lodash/differenceWith'

export default {
  name: 'SelectSingleInput',

  components: { ValidationProvider },

  props: {
    label: {
      type: String,
      rquired: true,
    },
    value: {
      type: [String, Number],
      required: false,
      default: '',
    },
    items: {
      type: Array,
      required: true,
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    itemText: {
      type: String,
      required: false,
      default: '',
    },
    itemValue: {
      type: String,
      required: false,
      default: '',
    },
    clearable: {
      type: Boolean,
      required: false,
      default: true,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    debounce: {
      type: Number,
      required: false,
      default: 0,
    },
    vid: {
      // 動的追加した画面項目に対してvee-vadalidateを動作させる場合に指定。
      // 画面で一意になるIDを振る。
      type: String,
      required: false,
      default: '',
    },
    exceptCdList: {
      // 選択肢から除外したいIDのリスト
      type: Array,
      required: false,
      default: () => [],
    },
    exceptComparator: {
      // 選択肢から除外したい対象を求めるためのメソッド
      type: Function,
      required: false,
      default: () => {},
    },
    isSelectedAuto: {
      // 選択肢が1件の場合、自動選択する
      type: Boolean,
      required: false,
      default: true,
    },
  },

  watch: {
    items() {
      if (
        this.isSelectedAuto &&
        !this.disabled &&
        this.required &&
        this.items.length == 1 &&
        this.model != this.items[0][`${this.itemValue}`]
      ) {
        this.model = this.items[0][`${this.itemValue}`]
        this.onChange(this.model)
      }
    },
  },

  data() {
    return {
      timerId: undefined,
    }
  },

  computed: {
    model: {
      get() {
        return this.containsSelectedValue ? this.value : ''
      },
      set(value) {
        this.$emit('input', value ? value : '')
      },
    },
    fullLabel() {
      return this.required ? `${this.label}  ${this.$t('ELE0024')}` : this.label
    },
    itemsComputed: {
      get() {
        let items = this.items
        // 除外指定のある選択肢をitemsから削除
        if (this.exceptCdList && this.exceptCdList.length) {
          items = differenceWith(items, this.exceptCdList, this.exceptComparator)
        }
        return items
      },
    },
    rulesComputed() {
      if (this.rules && this.required) {
        return `required|${this.rules}`
      } else if (this.required) {
        return 'required'
      } else if (this.rules) {
        return this.rules
      } else {
        return ''
      }
    },
    containsSelectedValue() {
      // value値がプルダウン選択肢に含まれるか判定
      return this.items.some((e) => e[this.itemValue] == this.value)
    },
  },

  methods: {
    customFilter(item, queryText) {
      // 入力されたテキストでプルダウン候補絞り込み (ID,Nameで部分一致検索)
      const id = String(item[this.itemValue]).toLocaleLowerCase()
      const name = String(item[this.itemText]).toLocaleLowerCase()
      const query = queryText.toLocaleLowerCase()
      return id.indexOf(query) > -1 || name.indexOf(query) > -1
    },
    onChange(e) {
      this.$emit('change', e)
    },
    onSearchInput(e) {
      // 最後の入力から指定秒経過後に親へemit
      clearTimeout(this.timerId)
      this.timerId = setTimeout(async () => {
        this.$emit('searchInput', e)
      }, 500)
    },
  },
}
</script>
<style>
/** 背景色指定 */
.select-single-input:not(.v-input--is-disabled) > .v-input__control > .v-input__slot {
  background: #edeffd;
  margin-bottom: 2px;
}
/** ボーダー色指定 */
.select-single-input:not(.v-input--is-disabled) > .v-input__control > .v-input__slot > fieldset {
  border-style: solid;
  border-width: 2px;
  border-color: var(--v-primary-base);
}
/* 非活性のラベル色 */
.select-single-input.v-input--is-disabled > .v-input__control > .v-input__slot > .v-select__slot > .v-label {
  color: #000000;
}
/* フォーカスが外れている場合のラベル色 */
.select-single-input:not(.v-input--is-disabled) > .v-input__control > .v-input__slot > .v-select__slot > .v-label {
  color: var(--v-primary-base);
}
/* 非活性の選択値のフォント色 */
.select-single-input.v-input--is-disabled > .v-input__control > .v-input__slot > .v-select__slot input {
  color: #000000;
}
/** エラーメッセージの上端が欠けるのを回避 */
.select-single-input > .v-input__control .v-messages {
  margin-top: 1px;
}
/** ローディングの表示位置調整(dense利用時にはあった方がよい) */
.select-single-input .v-progress-linear {
  margin-top: 1px;
}
</style>
